import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  LockClosedIcon,
  KeyIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { onboardingApi } from '@/api/onboardingApi'
import toast from 'react-hot-toast'

// Form schemas for onboarding steps
const passwordSetupSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
  confirmPassword: z.string()
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

const emailVerificationSchema = z.object({
  verificationCode: z.string()
    .length(6, 'Code must be exactly 6 digits')
    .regex(/^\d{6}$/, 'Code must contain only numbers'),
})

const pinSetupSchema = z.object({
  pin: z.string()
    .length(4, 'PIN must be exactly 4 digits')
    .regex(/^\d{4}$/, 'PIN must contain only numbers'),
  confirmPin: z.string()
    .length(4, 'PIN must be exactly 4 digits'),
}).refine(data => data.pin === data.confirmPin, {
  message: "PINs don't match",
  path: ["confirmPin"],
})

type PasswordSetupData = z.infer<typeof passwordSetupSchema>
type EmailVerificationData = z.infer<typeof emailVerificationSchema>
type PinSetupData = z.infer<typeof pinSetupSchema>

export default function UserOnboardingPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Get onboarding status from login response
  const onboardingStatus = location.state?.onboardingStatus
  const initialStep = location.state?.nextStep || 'password_setup'
  
  const [currentStep, setCurrentStep] = useState<'password_setup' | 'email_verification' | 'pin_setup' | 'complete'>(initialStep)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showPIN, setShowPIN] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  // Forms for each step
  const passwordForm = useForm<PasswordSetupData>({
    resolver: zodResolver(passwordSetupSchema)
  })

  const emailForm = useForm<EmailVerificationData>({
    resolver: zodResolver(emailVerificationSchema)
  })

  const pinForm = useForm<PinSetupData>({
    resolver: zodResolver(pinSetupSchema)
  })

  // Helper function to get redirect path based on user role
  const getRedirectPath = (userRole: string): string => {
    const role = userRole?.toLowerCase() || ''

    // Check for super admin
    if (role === 'super_admin' || role.includes('super') || role.includes('platform')) {
      return '/admin/dashboard'
    }

    // Check for school admin
    if (role === 'school_admin' || (role.includes('school') && role.includes('admin'))) {
      return '/schoolAdmin/dashboard'
    }

    // Check for teacher
    if (role === 'teacher' || role.includes('teacher')) {
      return '/teacher/dashboard'
    }

    // Check for parent
    if (role === 'parent' || role.includes('parent') || role.includes('guardian')) {
      return '/parent/dashboard'
    }

    // Check for driver
    if (role === 'driver' || role.includes('driver')) {
      return '/driver/dashboard'
    }

    // Check for student
    if (role === 'student' || role.includes('student') || role.includes('pupil') || role.includes('learner')) {
      return '/student/dashboard'
    }

    // Default fallback to school admin dashboard
    return '/schoolAdmin/dashboard'
  }

  // Password setup handler
  const handlePasswordSetup = async (data: PasswordSetupData) => {
    setLoading(true)
    try {
      const result = await onboardingApi.setupPassword(data)
      
      toast.success(result.message)
      setCurrentStep('email_verification')
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update password')
    } finally {
      setLoading(false)
    }
  }

  // Send email verification
  const handleSendVerification = async () => {
    setLoading(true)
    try {
      const result = await onboardingApi.sendEmailVerification()
      
      setEmailSent(true)
      toast.success(result.message)
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send verification code')
    } finally {
      setLoading(false)
    }
  }

  // Resend verification code
  const handleResendVerification = async () => {
    if (resendCooldown > 0) return
    
    setResendLoading(true)
    try {
      await onboardingApi.sendEmailVerification()
      
      toast.success('Verification code resent successfully!')
      
      // Reset the form
      emailForm.reset()
      
      // Start cooldown (60 seconds)
      setResendCooldown(60)
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resend verification code')
    } finally {
      setResendLoading(false)
    }
  }

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Verify email handler
  const handleEmailVerification = async (data: EmailVerificationData) => {
    setLoading(true)
    try {
      const result = await onboardingApi.verifyEmail(data)
      
      toast.success(result.message)
      setCurrentStep('pin_setup')
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid verification code')
    } finally {
      setLoading(false)
    }
  }

  // PIN setup handler
  const handlePinSetup = async (data: PinSetupData) => {
    setLoading(true)
    try {
      const result = await onboardingApi.setupPin(data)
      
      toast.success(result.message)
      
      // Redirect based on role
      const redirectPath = getRedirectPath(user?.role || 'SUPER_ADMIN')
      setTimeout(() => {
        navigate(redirectPath, { replace: true })
      }, 1500)
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to setup PIN')
    } finally {
      setLoading(false)
    }
  }

  // Skip to next step
  const skipStep = () => {
    if (currentStep === 'password_setup') {
      setCurrentStep('email_verification')
    } else if (currentStep === 'email_verification') {
      setCurrentStep('pin_setup')
    } else {
      // Complete onboarding
      toast.success('Setup completed! Welcome to SmartTag School.')
      const redirectPath = getRedirectPath(user?.role || 'SUPER_ADMIN')
      navigate(redirectPath, { replace: true })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Complete Your Setup</h1>
                <p className="text-blue-100">Welcome {user?.firstName}! Let's secure your account</p>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="px-8 py-4 bg-gray-50 border-b">
            <div className="flex items-center justify-between">
              {['password_setup', 'email_verification', 'pin_setup'].map((step, index) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    currentStep === step ? 'bg-blue-600 text-white' : 
                    ['password_setup', 'email_verification', 'pin_setup'].indexOf(currentStep) > index ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {['password_setup', 'email_verification', 'pin_setup'].indexOf(currentStep) > index ? 
                      <CheckCircleIcon className="h-4 w-4" /> : index + 1
                    }
                  </div>
                  {index < 2 && <ArrowRightIcon className="h-4 w-4 text-gray-400 mx-2" />}
                </div>
              ))}
            </div>
            <div className="text-center mt-2">
              <span className="text-sm text-gray-600 capitalize">
                {currentStep.replace('_', ' ')}
              </span>
            </div>
          </div>

          <div className="p-8">
            <AnimatePresence mode="wait">
              {/* Password Setup Step */}
              {currentStep === 'password_setup' && (
                <motion.div
                  key="password-setup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="text-center mb-6">
                    <KeyIcon className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-gray-900">Update Your Password</h2>
                    <p className="text-gray-600">Change from your temporary password to a secure one</p>
                  </div>

                  <form onSubmit={passwordForm.handleSubmit(handlePasswordSetup)} className="space-y-4">
                    <Input
                      {...passwordForm.register('currentPassword')}
                      type="password"
                      label="Current Password"
                      placeholder="Enter temporary password"
                      error={passwordForm.formState.errors.currentPassword?.message}
                    />
                    
                    <Input
                      {...passwordForm.register('newPassword')}
                      type={showPassword ? 'text' : 'password'}
                      label="New Password"
                      placeholder="Enter new secure password"
                      error={passwordForm.formState.errors.newPassword?.message}
                    />
                    
                    <Input
                      {...passwordForm.register('confirmPassword')}
                      type={showPassword ? 'text' : 'password'}
                      label="Confirm New Password"
                      placeholder="Repeat new password"
                      error={passwordForm.formState.errors.confirmPassword?.message}
                    />

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="showPassword"
                        checked={showPassword}
                        onChange={(e) => setShowPassword(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="showPassword" className="ml-2 text-sm text-gray-700">
                        Show passwords
                      </label>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </Button>

                    <button
                      type="button"
                      onClick={skipStep}
                      className="w-full text-sm text-gray-600 hover:text-gray-800"
                    >
                      Skip for now →
                    </button>
                  </form>
                </motion.div>
              )}

              {/* Email Verification Step */}
              {currentStep === 'email_verification' && (
                <motion.div
                  key="email-verification"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="text-center mb-6">
                    <EnvelopeIcon className="h-12 w-12 text-green-600 mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-gray-900">Verify Your Email</h2>
                    <p className="text-gray-600">We'll send a code to <span className="font-medium">{user?.email}</span></p>
                  </div>

                  {!emailSent ? (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <EnvelopeIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-medium text-blue-900">Email Verification Required</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Verify your email to receive notifications and security alerts.
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleSendVerification}
                        className="w-full bg-green-600 hover:bg-green-700"
                        disabled={loading}
                      >
                        {loading ? 'Sending...' : 'Send Verification Code'}
                      </Button>

                      <button
                        onClick={skipStep}
                        className="w-full text-sm text-gray-600 hover:text-gray-800"
                      >
                        Skip verification for now →
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={emailForm.handleSubmit(handleEmailVerification)} className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto mb-2" />
                        <p className="text-sm text-green-900 text-center">Code sent to your email!</p>
                      </div>

                      <Input
                        {...emailForm.register('verificationCode')}
                        label="Verification Code"
                        placeholder="Enter 6-digit code"
                        className="text-center text-lg font-mono tracking-widest"
                        maxLength={6}
                        error={emailForm.formState.errors.verificationCode?.message}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                          emailForm.setValue('verificationCode', value)
                        }}
                      />

                      <div className="flex space-x-3">
                        <Button
                          type="button"
                          onClick={handleResendVerification}
                          variant="secondary"
                          className="flex-1"
                          disabled={resendLoading || loading || resendCooldown > 0}
                        >
                          {resendLoading 
                            ? 'Resending...' 
                            : resendCooldown > 0 
                              ? `Resend (${resendCooldown}s)` 
                              : 'Resend Code'
                          }
                        </Button>
                        
                        <Button
                          type="submit"
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          disabled={loading}
                        >
                          {loading ? 'Verifying...' : 'Verify Email'}
                        </Button>
                      </div>

                      <button
                        type="button"
                        onClick={skipStep}
                        className="w-full text-sm text-gray-600 hover:text-gray-800"
                      >
                        Skip for now →
                      </button>
                    </form>
                  )}
                </motion.div>
              )}

              {/* PIN Setup Step */}
              {currentStep === 'pin_setup' && (
                <motion.div
                  key="pin-setup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="text-center mb-6">
                    <LockClosedIcon className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-gray-900">Create Your PIN</h2>
                    <p className="text-gray-600">Set up a 4-digit PIN for quick access</p>
                  </div>

                  <form onSubmit={pinForm.handleSubmit(handlePinSetup)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        {...pinForm.register('pin')}
                        type={showPIN ? 'text' : 'password'}
                        label="Create PIN"
                        placeholder="4 digits"
                        maxLength={4}
                        className="text-center text-lg font-mono tracking-widest"
                        error={pinForm.formState.errors.pin?.message}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                          pinForm.setValue('pin', value)
                        }}
                      />

                      <Input
                        {...pinForm.register('confirmPin')}
                        type={showPIN ? 'text' : 'password'}
                        label="Confirm PIN"
                        placeholder="4 digits"
                        maxLength={4}
                        className="text-center text-lg font-mono tracking-widest"
                        error={pinForm.formState.errors.confirmPin?.message}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                          pinForm.setValue('confirmPin', value)
                        }}
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="showPIN"
                        checked={showPIN}
                        onChange={(e) => setShowPIN(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="showPIN" className="ml-2 text-sm text-gray-700">
                        Show PIN
                      </label>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mx-auto mb-2" />
                      <p className="text-sm text-yellow-700 text-center">
                        Avoid weak PINs like 1234, 0000, or repeating numbers
                      </p>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      disabled={loading}
                    >
                      {loading ? 'Setting up...' : 'Complete Setup'}
                    </Button>

                    <button
                      type="button"
                      onClick={skipStep}
                      className="w-full text-sm text-gray-600 hover:text-gray-800"
                    >
                      Skip for now →
                    </button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  )
}